import { popup } from '@/js/popup'
import proj4 from 'proj4'
import store from '@/store'
import axios from 'axios'

/* ======== GCPワープ改善ユーティリティ ======== */
export function lngLatToMerc([lng, lat]) {
  const x = (lng * 20037508.34) / 180
  const y = Math.log(Math.tan(((90 + lat) * Math.PI) / 360)) / (Math.PI / 180)
  const yMeters = (y * 20037508.34) / 180
  return [x, yMeters]
}
export function imageCssToNatural([x, y], img) {
  const sx = img.naturalWidth / (img.clientWidth || img.naturalWidth)
  const sy = img.naturalHeight / (img.clientHeight || img.naturalHeight)
  return [x * sx, y * sy]
}
export function fitSimilarity2P(srcPts, dstPts) {
  // 2点→スケール一様・回転・平行移動（人の感覚に近い）
  const [x1, y1] = srcPts[0],
    [x2, y2] = srcPts[1]
  const [X1, Y1] = dstPts[0],
    [X2, Y2] = dstPts[1]
  const dxs = x2 - x1,
    dys = y2 - y1
  const dxt = X2 - X1,
    dyt = Y2 - Y1
  const ds = Math.hypot(dxs, dys) || 1
  const dt = Math.hypot(dxt, dyt) || 1
  const s = dt / ds // 一様スケール
  const th = Math.atan2(dyt, dxt) - Math.atan2(dys, dxs) // 回転差
  const cos = Math.cos(th),
    sin = Math.sin(th)
  // 変換: [X Y]^T = s * R * [x y]^T + t
  const A = s * cos,
    B = s * -sin
  const D = s * sin,
    E = s * cos
  const C = X1 - (A * x1 + B * y1)
  const F = Y1 - (D * x1 + E * y1)
  return [A, B, C, D, E, F]
}
export function fitAffineN(srcPts, dstPts, w) {
  // N>=3 の加重最小二乗（アフィン6係数）
  const n = srcPts.length
  const M = 6
  const A = Array.from({ length: M }, () => Array(M).fill(0))
  const b = Array(M).fill(0)
  for (let i = 0; i < n; i++) {
    const wi = w ? w[i] : 1
    const [x, y] = srcPts[i]
    const [X, Y] = dstPts[i]
    const rowX = [x, y, 1, 0, 0, 0].map((v) => wi * v)
    const rowY = [0, 0, 0, x, y, 1].map((v) => wi * v)
    for (let j = 0; j < M; j++) {
      for (let k = 0; k < M; k++) {
        A[j][k] += rowX[j] * rowX[k] + rowY[j] * rowY[k]
      }
      b[j] += rowX[j] * X + rowY[j] * Y
    }
  }
  // ガウス消去
  const x = b.slice()
  const N = A.length
  const B = A.map((r) => r.slice())
  for (let i = 0; i < N; i++) {
    // pivot
    let p = i
    for (let r = i + 1; r < N; r++) {
      if (Math.abs(B[r][i]) > Math.abs(B[p][i])) p = r
    }
    if (p !== i) {
      ;[B[i], B[p]] = [B[p], B[i]]
      ;[x[i], x[p]] = [x[p], x[i]]
    }
    const diag = B[i][i] || 1e-12
    for (let j = i + 1; j < N; j++) {
      const f = B[j][i] / diag
      for (let k = i; k < N; k++) B[j][k] -= f * B[i][k]
      x[j] -= f * x[i]
    }
  }
  for (let i = N - 1; i >= 0; i--) {
    let s = 0
    for (let j = i + 1; j < N; j++) s += B[i][j] * x[j]
    x[i] = (x[i] - s) / (B[i][i] || 1e-12)
  }
  return x // [A,B,C,D,E,F]
}
export function huberWeights(res, delta) {
  return res.map((r) => {
    const a = Math.abs(r)
    return a <= delta ? 1 : delta / a
  })
}
export function fitAffineRobust(srcPts, dstPts, iters = 3) {
  let w = Array(srcPts.length).fill(1)
  let coeff = fitAffineN(srcPts, dstPts, w)
  for (let t = 0; t < iters; t++) {
    // 残差
    const res = srcPts.map(([x, y], i) => {
      const [A, B, C, D, E, F] = coeff
      const Xp = A * x + B * y + C
      const Yp = D * x + E * y + F
      const [X, Y] = dstPts[i]
      return Math.hypot(Xp - X, Yp - Y)
    })
    w = huberWeights(res, Math.max(1, 1.4826 * median(res))) // ロバスト
    coeff = fitAffineN(srcPts, dstPts, w)
  }
  return coeff
}
export function median(arr) {
  const s = [...arr].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}
export function applyAffine([A, B, C, D, E, F], [x, y]) {
  return [A * x + B * y + C, D * x + E * y + F]
}
export function composeAffine([A1, B1, C1, D1, E1, F1], [A2, B2, C2, D2, E2, F2]) {
  // M = M1 ∘ M2
  const A = A1 * A2 + B1 * D2
  const B = A1 * B2 + B1 * E2
  const C = A1 * C2 + B1 * F2 + C1
  const D = D1 * A2 + E1 * D2
  const E = D1 * B2 + E1 * E2
  const F = D1 * C2 + E1 * F2 + F1
  return [A, B, C, D, E, F]
}
export function previewOnCanvas(img, canvas, M) {
  const ctx = canvas.getContext('2d')
  const w = img.naturalWidth,
    h = img.naturalHeight
  // 変換後の4隅
  const corners = [
    [0, 0],
    [w, 0],
    [w, h],
    [0, h],
  ].map((p) => applyAffine(M, p))
  const xs = corners.map((p) => p[0]),
    ys = corners.map((p) => p[1])
  const minX = Math.min(...xs),
    maxX = Math.max(...xs),
    minY = Math.min(...ys),
    maxY = Math.max(...ys)

  // キャンバスは表示サイズに合わせる（非表示時は natural を使う）
  const cw = Math.max(1, img.clientWidth || img.naturalWidth)
  const ch = Math.max(1, img.clientHeight || img.naturalHeight)
  canvas.width = cw
  canvas.height = ch

  const W = Math.max(1, maxX - minX)
  const H = Math.max(1, maxY - minY)
  const s = Math.min(cw / W, ch / H) // 一様スケール

  // ★ 重要：キャンバスは +Y が下向き。地図（メルカトル）は +Y が上向き。
  // そのため Y 軸を反転して合わせる。
  // T: world(m) → canvas(px)
  const T = [s, 0, -minX * s, 0, -s, maxY * s]

  const Mv = composeAffine(T, M) // v' = T(M(v))
  const [A, B, C, D, E, F] = Mv
  ctx.setTransform(A, D, B, E, C, F)
  ctx.clearRect(0, 0, canvas.width, canvas.height)
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, 0, 0)
}
export function worldFileFromAffine([A, B, C, D, E, F]) {
  // ワールドファイル仕様：
  // 1:A(px→X) 2:D 3:B 4:E 5:C 6:F （上から順）
  return `${A}\n${D}\n${B}\n${E}\n${C}\n${F}`
}

/* ======== GCPワープ改善ユーティリティ ======== */

export function onLineClick(e, map, map2Flg) {
  popup(e, map, 'map01', map2Flg)
}

export function xmlToGeojson(xmlString) {
  const parser = new DOMParser()
  const xmlDoc = parser.parseFromString(xmlString, 'text/xml')

  const geojson = {
    type: 'FeatureCollection',
    features: [],
  }

  const namespaceURI = 'http://www.moj.go.jp/MINJI/tizuxml'
  const zmnNamespaceURI = 'http://www.moj.go.jp/MINJI/tizuzumen'
  const spatialAttributes = xmlDoc.getElementsByTagNameNS(namespaceURI, '空間属性')[0]

  if (!spatialAttributes) {
    console.error('空間属性が見つかりません')
    return geojson
  }

  const surfaces = spatialAttributes.getElementsByTagNameNS(zmnNamespaceURI, 'GM_Surface')

  for (let surface of surfaces) {
    const coordinates = parseSurfaceCoordinates(surface, xmlDoc, zmnNamespaceURI)
    if (coordinates.length > 0) {
      geojson.features.push({
        type: 'Feature',
        geometry: {
          type: 'Polygon',
          coordinates: coordinates, // ネスト修正
        },
        properties: parseThematicAttributes(xmlDoc, namespaceURI),
      })
    }
  }

  return geojson
}

export function parseSurfaceCoordinates(surface, xmlDoc, namespaceURI) {
  if (!surface) return []

  const polygons = []
  const surfaceBoundary = surface.getElementsByTagNameNS(namespaceURI, 'GM_SurfaceBoundary')[0]
  if (!surfaceBoundary) return []

  const exterior = surfaceBoundary.getElementsByTagNameNS(namespaceURI, 'GM_SurfaceBoundary.exterior')[0]
  if (!exterior) return []

  const rings = exterior.getElementsByTagNameNS(namespaceURI, 'GM_Ring')
  if (rings.length === 0) return []

  const exteriorRing = parseRing(rings[0], xmlDoc, namespaceURI)
  if (exteriorRing.length > 0) {
    const polygon = [exteriorRing]

    const interiors = surfaceBoundary.getElementsByTagNameNS(namespaceURI, 'GM_SurfaceBoundary.interior')
    for (let interior of interiors) {
      const interiorRings = interior.getElementsByTagNameNS(namespaceURI, 'GM_Ring')
      for (let ring of interiorRings) {
        const hole = parseRing(ring, xmlDoc, namespaceURI)
        if (hole.length > 0) polygon.push(hole)
      }
    }

    return polygon // ネストを修正
  }

  return []
}

export function parseRing(ring, xmlDoc, namespaceURI) {
  if (!ring) return []

  const points = []
  const generators = ring.getElementsByTagNameNS(namespaceURI, 'GM_CompositeCurve.generator')

  for (let generator of generators) {
    const curveId = generator.getAttribute('idref')
    if (!curveId) continue

    const curve = xmlDoc.getElementById(curveId)
    if (!curve) continue

    const segments = curve.getElementsByTagNameNS(namespaceURI, 'GM_Curve.segment')
    for (let segment of segments) {
      const controlPoints = segment.getElementsByTagNameNS(namespaceURI, 'GM_LineString.controlPoint')
      for (let pointArray of controlPoints) {
        const columns = pointArray.getElementsByTagNameNS(namespaceURI, 'GM_PointArray.column')
        for (let column of columns) {
          const pointRef = column.getElementsByTagNameNS(namespaceURI, 'GM_PointRef.point')[0]
          if (!pointRef) continue

          const pointId = pointRef.getAttribute('idref')
          if (!pointId) continue

          const point = xmlDoc.getElementById(pointId)
          if (!point) continue

          const directPos = point.getElementsByTagNameNS(namespaceURI, 'DirectPosition')[0]
          if (!directPos) continue

          const xElem = directPos.getElementsByTagNameNS(namespaceURI, 'X')[0]
          const yElem = directPos.getElementsByTagNameNS(namespaceURI, 'Y')[0]
          if (xElem && yElem) {
            const x = parseFloat(xElem.textContent)
            const y = parseFloat(yElem.textContent)
            if (points.length === 0 || points[points.length - 1][0] !== y || points[points.length - 1][1] !== x) {
              points.push([y, x])
            }
          }
        }
      }
    }
  }

  if (
    points.length > 1 &&
    (points[0][0] !== points[points.length - 1][0] || points[0][1] !== points[points.length - 1][1])
  ) {
    points.push(points[0])
  }

  return points
}

export function parseThematicAttributes(xmlDoc, namespaceURI) {
  return {
    地図名: xmlDoc.getElementsByTagNameNS(namespaceURI, '地図名')[0]?.textContent || '不明',
    座標系: xmlDoc.getElementsByTagNameNS(namespaceURI, '座標系')[0]?.textContent || '不明',
    市区町村名: xmlDoc.getElementsByTagNameNS(namespaceURI, '市区町村名')[0]?.textContent || '不明',
  }
}

export const transformCoordinates = (coordinates) => {
  const code = store.state.zahyokei.find((item) => item.kei === store.state.zahyokei).code
  return proj4(code, 'EPSG:4326', coordinates)
}

const popups = []
// export function closeAllPopups() {
//   popups.forEach(popup => popup.remove())
//   // 配列をクリア
//   popups.length = 0
// }
// 緯度経度をタイル座標 (z, x, y) に変換
export function lonLatToTile(lon, lat, zoom) {
  const n = Math.pow(2, zoom)
  const xTile = Math.floor(((lon + 180.0) / 360.0) * n)
  const yTile = Math.floor(
    ((1.0 - Math.log(Math.tan((lat * Math.PI) / 180.0) + 1.0 / Math.cos((lat * Math.PI) / 180.0)) / Math.PI) / 2.0) * n
  )
  return { xTile, yTile }
}

// タイル内のピクセル座標を計算
export function calculatePixelInTile(lon, lat, zoom, xTile, yTile, tileSize = 256) {
  const n = Math.pow(2, zoom)
  const x = (((lon + 180.0) / 360.0) * n - xTile) * tileSize
  const y =
    (((1.0 - Math.log(Math.tan((lat * Math.PI) / 180.0) + 1.0 / Math.cos((lat * Math.PI) / 180.0)) / Math.PI) / 2.0) *
      n -
      yTile) *
    tileSize
  return { x: Math.floor(x), y: Math.floor(y) }
}

// 修正版: RGB値を標高にデコードする関数
export function decodeElevationFromRGB(r, g, b) {
  const scale = 0.01 // スケール値（仮定）
  const offset = 0 // オフセット値（仮定）
  return (r * 256 * 256 + g * 256 + b) * scale + offset // RGB値を計算
}

// 標高データをタイル画像から取得し、キャンバスに出力
export async function fetchElevationFromImage(imageUrl, lon, lat, zoom) {
  const { xTile, yTile } = lonLatToTile(lon, lat, zoom)

  const img = new Image()
  img.crossOrigin = 'Anonymous' // クロスオリジン対応
  img.src = imageUrl

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.style.display = 'none'
      const ctx = canvas.getContext('2d')
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      // タイル内のピクセル座標を計算
      const { x, y } = calculatePixelInTile(lon, lat, zoom, xTile, yTile, img.width)

      // ピクセルデータを取得
      const imageData = ctx.getImageData(x, y, 1, 1).data
      const [r, g, b] = imageData // R, G, B 値を取得
      const elevation = decodeElevationFromRGB(r, g, b) // 標高を計算

      // キャンバスに描画
      ctx.fillStyle = 'red'
      ctx.beginPath()
      ctx.arc(x, y, 5, 0, 2 * Math.PI)
      ctx.fill()
      ctx.font = '12px Arial'
      ctx.fillStyle = 'white'
      ctx.fillText(`${elevation}m`, x + 10, y - 10)
      document.body.appendChild(canvas)

      resolve(elevation)
    }

    img.onerror = (err) => {
      reject(`画像の読み込みに失敗しました: ${err}`)
    }
  })
}

// 標高を取得する関数
export async function fetchElevation(lon, lat, zoom = 15) {
  const baseUrl = 'https://tiles.gsj.jp/tiles/elev/mixed/{z}/{y}/{x}.png'
  const { xTile, yTile } = lonLatToTile(lon, lat, zoom)
  // y と x を反転してタイルURLを生成
  const tileUrl = baseUrl.replace('{z}', zoom).replace('{x}', xTile).replace('{y}', yTile)
  try {
    const elevation = await fetchElevationFromImage(tileUrl, lon, lat, zoom)
    return elevation
  } catch (error) {
    // console.error("エラー:", error);
  }
}

export function fitSimilarity2PExactDown(p1d, p2d, q1, q2) {
  // 画像は y 下向き → いったん y 上向きにして厳密相似を解く
  const p1 = [p1d[0], -p1d[1]],
    p2 = [p2d[0], -p2d[1]]
  const [x1, y1] = p1,
    [x2, y2] = p2
  const [X1, Y1] = q1,
    [X2, Y2] = q2
  const ds = Math.hypot(x2 - x1, y2 - y1) || 1e-12,
    dt = Math.hypot(X2 - X1, Y2 - Y1) || 1e-12
  const s = dt / ds,
    th = Math.atan2(Y2 - Y1, X2 - X1) - Math.atan2(y2 - y1, x2 - x1)
  const c = Math.cos(th),
    sn = Math.sin(th)
  const Au = s * c,
    Bu = -s * sn,
    Du = s * sn,
    Eu = s * c
  const Cu = X1 - (Au * x1 + Bu * y1)
  const Fu = Y1 - (Du * x1 + Eu * y1)
  const FLIP_Y = [1, 0, 0, 0, -1, 0] // y_down → y_up
  return composeAffine([Au, Bu, Cu, Du, Eu, Fu], FLIP_Y) // y_down → 世界
}

// クリック座標から snapPx ピクセル以内にある “Point / Polygon の頂点” にスナップ
// 見つからなければ null を返す（= ラインは描かない）
// layerIds を絞りたい場合は第4引数に配列を渡せます（省略で全レイヤ対象）

export function pickNearestVertex(map, lngLat, snapPx = 16, layerIds = null) {
  if (!map || !lngLat) return null
  const p = map.project(lngLat)
  const bbox = [
    { x: p.x - snapPx, y: p.y - snapPx },
    { x: p.x + snapPx, y: p.y + snapPx },
  ]

  let features = []
  try {
    features = map.queryRenderedFeatures([bbox[0], bbox[1]], layerIds ? { layers: layerIds } : {})
  } catch (_) {
    features = map.queryRenderedFeatures([bbox[0], bbox[1]])
  }
  if (!features.length) return null

  let best = null,
    bestDist = Infinity,
    bestProps = null

  const pushCandidate = (coord, props) => {
    const pp = map.project({ lng: coord[0], lat: coord[1] })
    const d = Math.hypot(pp.x - p.x, pp.y - p.y)
    if (d < bestDist) {
      bestDist = d
      best = { lng: coord[0], lat: coord[1] }
      bestProps = props || null
    }
  }

  for (const f of features) {
    const g = f?.geometry
    if (!g) continue
    const t = g.type,
      props = f.properties || {}
    if (t === 'Point') {
      pushCandidate(g.coordinates, props)
      continue
    }
    if (t === 'MultiPoint') {
      for (const c of g.coordinates) pushCandidate(c, props)
      continue
    }
    if (t === 'Polygon') {
      for (const ring of g.coordinates) for (const c of ring) pushCandidate(c, props)
      continue
    }
    if (t === 'MultiPolygon') {
      for (const poly of g.coordinates) for (const ring of poly) for (const c of ring) pushCandidate(c, props)
      continue
    }
    // LineString 系は対象外
  }
  if (!best || bestDist > snapPx) return null
  return { ...best, __anchor: true, __snapped: true, properties: bestProps }
}

// 系ラベル → EPSGコード
export function epsgFromZahyokei(label, zahyokeiList) {
  if (!label || !Array.isArray(zahyokeiList)) return null
  const hit = zahyokeiList.find((e) => e.kei === label)
  return hit?.code || null
}

// 文字/数値→数値
// 文字/数値→数値（空文字は null 扱い）
export function toNum(v) {
  if (v == null) return null
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  const s = String(v).trim().replace(/,/g, '')
  if (s === '') return null // ★ 追加：空文字は無効
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

// props から XY を優先順で抽出（無ければ null）
// props から XY を優先順で抽出（使ったキー名も返す）
export function xyFromProps(props) {
  if (!props) return null
  const candidates = [
    ['補正後X座標', '補正後Y座標'],
    ['X座標', 'Y座標'],
    ['X', 'Y'],
  ]
  for (const [kx, ky] of candidates) {
    const x = toNum(props[kx])
    const y = toNum(props[ky])
    if (x != null && y != null) {
      return { x, y, _meta: { keyX: kx, keyY: ky, valueX: x, valueY: y } }
    }
  }
  return null
}

// WGS84 -> 平面直角（アンカー側のみで使用）
// WGS84 -> 平面直角（proj4 は [E, N] を返すので {x:N, y:E} にスワップ）
export function xyFromLngLat(lng, lat, epsg) {
  const [E, N] = proj4('EPSG:4326', epsg, [lng, lat])
  if (!Number.isFinite(E) || !Number.isFinite(N)) return null
  return { x: N, y: E } // ★ X=北, Y=東 に統一
}
